import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GeoBrowseThresholds } from '../types';

const DEFAULT_PADJ = 0.05;
const DEFAULT_FC = 1;

export function parseThresholds(searchParams: URLSearchParams): GeoBrowseThresholds {
  const padj = Number(searchParams.get('padj') ?? DEFAULT_PADJ);
  const fc = Number(searchParams.get('fc') ?? DEFAULT_FC);
  return {
    padj: Number.isFinite(padj) ? padj : DEFAULT_PADJ,
    fc: Number.isFinite(fc) ? fc : DEFAULT_FC,
  };
}

export function useGeoBrowseThresholds() {
  const [searchParams, setSearchParams] = useSearchParams();
  const thresholds = parseThresholds(searchParams);
  const [draftPadj, setDraftPadj] = useState(String(thresholds.padj));
  const [draftFc, setDraftFc] = useState(String(thresholds.fc));
  const [lastRunMs, setLastRunMs] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setDraftPadj(String(thresholds.padj));
    setDraftFc(String(thresholds.fc));
  }, [thresholds.padj, thresholds.fc]);

  const applyThresholds = useCallback(() => {
    const padj = Number(draftPadj);
    const fc = Number(draftFc);
    if (!Number.isFinite(padj) || !Number.isFinite(fc)) return;

    const next = new URLSearchParams(searchParams);
    next.set('padj', String(padj));
    next.set('fc', String(fc));
    setSearchParams(next, { replace: true });
    setLastRunMs(520);
    setRefreshKey(k => k + 1);
  }, [draftPadj, draftFc, searchParams, setSearchParams]);

  const mergePathWithSearchParams = useCallback(
    (path: string, extraParams?: Record<string, string>) => {
      const [pathname, pathQuery = ''] = path.split('?');
      const merged = new URLSearchParams(searchParams);
      if (pathQuery) {
        new URLSearchParams(pathQuery).forEach((value, key) => merged.set(key, value));
      }
      if (extraParams) {
        Object.entries(extraParams).forEach(([key, value]) => merged.set(key, value));
      }
      const qs = merged.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [searchParams],
  );

  const buildPathWithThresholds = useCallback(
    (path: string) => mergePathWithSearchParams(path),
    [mergePathWithSearchParams],
  );

  const buildStudiesListPath = useCallback(
    (opts?: { cui?: string }) => {
      const params = new URLSearchParams(searchParams);
      params.delete('comparison_id');
      if (opts?.cui) {
        params.set('cui', opts.cui);
      } else {
        params.delete('cui');
      }
      const qs = params.toString();
      return qs ? `/geo-browse/studies?${qs}` : '/geo-browse/studies';
    },
    [searchParams],
  );

  return {
    thresholds,
    draftPadj,
    draftFc,
    setDraftPadj,
    setDraftFc,
    applyThresholds,
    lastRunMs,
    refreshKey,
    buildPathWithThresholds,
    mergePathWithSearchParams,
    buildStudiesListPath,
    searchParams,
    setSearchParams,
  };
}

export function useGeoBrowseQuery<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
  enabled = true,
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetcher()
      .then(result => {
        if (!cancelled) setData(result);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load data');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}
